import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'

import DesktopView from './components/DesktopView'
import SubstituteMaterialPanel from './components/SubstituteMaterialPanel'
import RemoveSubstituteMaterialDialog from './components/RemoveSubstituteMaterialDialog'
import ImportSubstituteMaterialsDialog from './components/ImportSubstituteMaterialsDialog'
import Toaster from '../common/Toaster'
import {
	loadSubstituteMaterials,
	addSubstituteMaterial,
	updateSubstituteMaterial,
	removeSubstituteMaterial,
	importSubstituteMaterials,
	isDuplicateMaterialError,
	isInvalidFileFormatError,
	isEndpointUnavailableError,
	isUnauthorizedError,
	buildUnauthorizedMessage,
	describeUnclassifiedError,
	buildImportResultMessage
} from '../../store/substituteMaterials/substituteMaterialsActions'
import { catalogPageSelector } from '../../store/substituteMaterials/substituteMaterialsSelectors'
import { isNullOrEmpty } from '../../store/helpers/StringHelper'

export const DUPLICATE_MATERIAL_MESSAGE = 'This discontinued material already has a substitute registered.'
export const INVALID_FILE_FORMAT_MESSAGE = 'The file does not have the required format. Select another file and try again.'
export const IMPORT_FAILED_MESSAGE = 'The import could not be completed. The catalog was left unchanged.'

/**
 * Shown when the server answers that it has no such operation.
 *
 * The five routes this view calls — read, add, edit, remove and import — are all served now, so
 * this is no longer the ordinary outcome it was while the server side was being built. It stays
 * as the honest answer for a portal deployed against a server that does not have them: saying
 * the operation failed would send the reader looking for a problem that is not theirs.
 *
 * Worth knowing about its reach: it is chosen from the response status (404, 405, 501), so it
 * needs the response to arrive. A route that does not exist fails its CORS preflight first, and
 * the browser blocks the answer before axios can read a status — which lands on
 * NO_RESPONSE_MESSAGE instead. So the case this message was written for is precisely the one it
 * can miss, and the reader is told the service did not respond rather than that the operation
 * is unavailable.
 */
export const OPERATION_UNAVAILABLE_MESSAGE = 'This operation is not available on the server. The catalog can be consulted but not modified.'

/**
 * Substitute materials catalog index.
 *
 * The query criteria — search, sorting and page — lives in the URL, not in component state. The
 * handlers only push parameters; the effect below reacts to `location.search` and issues the
 * query. That is what makes the view linkable and what makes a browser reload land on the same
 * set of records.
 */
const Index = () => {
	const dispatch = useDispatch()
	const location = useLocation()
	const history = useHistory()

	const isLoading = useSelector((state) => state.substituteMaterials.get('isLoading'))
	const isSaving = useSelector((state) => state.substituteMaterials.get('isSaving'))
	const isImporting = useSelector((state) => state.substituteMaterials.get('isImporting'))
	const userProfile = useSelector((state) => state.session.get('profile'))

	const [query, setQuery] = useState('')
	const [toaster, setToaster] = useState({ open: false, variant: null, message: null })
	const [panelConfig, setPanelConfig] = useState({ opened: false, item: null })
	const [fieldError, setFieldError] = useState(null)
	const [removeConfig, setRemoveConfig] = useState({ opened: false, item: null })
	const [importOpened, setImportOpened] = useState(false)

	/**
	 * Read the catalog.
	 *
	 * The endpoint answers the whole catalog with no criteria on the wire, so this does not depend
	 * on the URL: changing the search, the order or the page re-derives the view from what is
	 * already loaded instead of asking again. It is dispatched on mount and after every mutation,
	 * and the action deliberately never short-circuits so a refresh always reaches the server.
	 */
	const reloadCatalog = useCallback(() => {
		return dispatch(loadSubstituteMaterials()).catch((error) => {
			setToaster({
				open: true,
				variant: 'warning',
				message: !isNullOrEmpty(error) ? error : 'An error occurred while loading the substitute materials catalog'
			})
		})
	}, [dispatch])

	useEffect(() => {
		reloadCatalog()
	}, [reloadCatalog])

	/**
	 * The criteria still lives in the URL — that is what keeps the view linkable and survives a
	 * reload — it just no longer triggers a request. The page is resolved from the loaded catalog.
	 */
	const criteria = queryString.parse(location.search)
	const { rows, pagination } = useSelector((state) => catalogPageSelector(state, criteria))

	/**Keep the search box in step with the URL, including on a direct entry carrying a query */
	useEffect(() => {
		const queryParameters = queryString.parse(location.search)
		setQuery(queryParameters.query ? queryParameters.query : '')
	}, [location.search])

	/**
	 * Push the search criteria to the URL. Clearing it removes the parameter rather than leaving
	 * an empty one behind, so a cleared search produces the same URL as never having searched.
	 */
	const onChangeSearchValue = (searchQuery) => {
		const queryParams = queryString.parse(location.search)
		if (searchQuery) {
			queryParams.query = searchQuery
			setQuery(searchQuery)
		} else {
			delete queryParams.query
			setQuery('')
		}
		delete queryParams.pageNumber
		history.push({ search: queryString.stringify(queryParams) })
	}

	const onChangeSortingCriteria = (sortCriteria) => {
		const queryParams = queryString.parse(location.search)
		queryParams.sortBy = sortCriteria.by
		queryParams.sortDirection = sortCriteria.direction
		// Reordering re-ranks the whole catalog, so the page the user was on no longer holds the
		// same rows. The page is clamped when the view is derived, but the URL would go on
		// claiming pageNumber=3 while showing something else -- the same reason the search does it.
		delete queryParams.pageNumber
		history.push({ search: queryString.stringify(queryParams) })
	}

	const onChangePage = (event, page) => {
		const queryParams = queryString.parse(location.search)
		queryParams.pageNumber = page
		history.push({ search: queryString.stringify(queryParams) })
	}

	const onOpenPanel = (item) => {
		setFieldError(null)
		setPanelConfig({ opened: true, item: item ? item : null })
	}

	const onClosePanel = () => {
		setFieldError(null)
		setPanelConfig({ opened: false, item: null })
	}

	/**
	 * Turn a rejected save into feedback.
	 *
	 * A discontinued material that already has a substitute is a problem with the code the user
	 * captured, so it is reported on that field and the panel stays open with the data intact.
	 * Anything else is an unexpected failure and goes to the toaster.
	 * @param {*} requestError
	 */
	const onSaveRejected = (requestError) => {
		if (isUnauthorizedError(requestError)) {
			setToaster({ open: true, variant: 'warning', message: buildUnauthorizedMessage(requestError) })
			return
		}
		if (isEndpointUnavailableError(requestError)) {
			setToaster({ open: true, variant: 'warning', message: OPERATION_UNAVAILABLE_MESSAGE })
			return
		}
		if (isDuplicateMaterialError(requestError)) {
			setFieldError({ name: 'ObsoleteMaterialCode', message: DUPLICATE_MATERIAL_MESSAGE })
			setToaster({ open: true, variant: 'warning', message: DUPLICATE_MATERIAL_MESSAGE })
			return
		}
		setToaster({
			open: true,
			variant: 'warning',
			message: describeUnclassifiedError(requestError, 'The substitute material could not be saved')
		})
	}

	const onSave = (model) => {
		setFieldError(null)
		let operation = model.Token ? updateSubstituteMaterial(model) : addSubstituteMaterial(model)
		let successMessage = model.Token ? 'Substitute material updated successfully' : 'Substitute material added successfully'

		return dispatch(operation)
			.then(() => {
				setToaster({ open: true, variant: 'success', message: successMessage })
				onClosePanel()
				return reloadCatalog()
			})
			.catch(onSaveRejected)
	}

	const onOpenRemove = (item) => setRemoveConfig({ opened: true, item })
	const onCloseRemove = () => setRemoveConfig({ opened: false, item: null })

	const onConfirmRemove = () => {
		let item = removeConfig.item
		return dispatch(removeSubstituteMaterial(item ? item.Token : ''))
			.then(() => {
				setToaster({ open: true, variant: 'success', message: 'Substitute material removed successfully' })
				onCloseRemove()
				return reloadCatalog()
			})
			.catch((requestError) => {
				if (isUnauthorizedError(requestError)) {
					setToaster({ open: true, variant: 'warning', message: buildUnauthorizedMessage(requestError) })
					return
				}
				if (isEndpointUnavailableError(requestError)) {
					setToaster({ open: true, variant: 'warning', message: OPERATION_UNAVAILABLE_MESSAGE })
					return
				}
				setToaster({
					open: true,
					variant: 'warning',
					message: describeUnclassifiedError(requestError, 'The substitute material could not be removed')
				})
			})
	}

	/**
	 * Report the aggregate result, then refresh — in that order.
	 *
	 * If the refresh were to fail, the user already knows what happened to their file. The refresh
	 * itself keeps the criteria the user had applied, which is why it goes through the explicit
	 * reload rather than through a URL change.
	 */
	const onImport = (file) => {
		return dispatch(importSubstituteMaterials(file))
			.then((result) => {
				setToaster({ open: true, variant: 'success', message: buildImportResultMessage(result) })
				setImportOpened(false)
				return reloadCatalog()
			})
			.catch((requestError) => {
				// The dialog closes: picking another file cannot fix a session, and leaving it open
				// invites the user to retry the one thing that is guaranteed to fail again.
				if (isUnauthorizedError(requestError)) {
					setToaster({ open: true, variant: 'warning', message: buildUnauthorizedMessage(requestError) })
					setImportOpened(false)
					return
				}
				if (isEndpointUnavailableError(requestError)) {
					setToaster({ open: true, variant: 'warning', message: OPERATION_UNAVAILABLE_MESSAGE })
					setImportOpened(false)
					return
				}
				// A file the server would not accept is the user's to fix, so the dialog stays open
				// for them to pick another one. Anything else ended the operation entirely.
				if (isInvalidFileFormatError(requestError)) {
					setToaster({ open: true, variant: 'warning', message: INVALID_FILE_FORMAT_MESSAGE })
					return
				}
				setToaster({
					open: true,
					variant: 'warning',
					message: describeUnclassifiedError(requestError, IMPORT_FAILED_MESSAGE)
				})
				setImportOpened(false)
			})
	}

	return (
		<>
			<DesktopView
				items={rows}
				pagination={pagination}
				isLoading={isLoading}
				isSaving={isSaving || isImporting}
				searchValue={query}
				userProfile={userProfile}
				onChangeSearchValue={onChangeSearchValue}
				onChangeSortingCriteria={onChangeSortingCriteria}
				onChangePage={onChangePage}
				onAddClick={() => onOpenPanel(null)}
				onImportClick={() => setImportOpened(true)}
				onEditClick={onOpenPanel}
				onRemoveClick={onOpenRemove}
			/>

			{panelConfig.opened && (
				<SubstituteMaterialPanel onClose={onClosePanel} onSave={onSave} isSaving={isSaving} item={panelConfig.item} fieldError={fieldError} />
			)}

			{removeConfig.opened && (
				<RemoveSubstituteMaterialDialog
					open={removeConfig.opened}
					onClose={onCloseRemove}
					onConfirm={onConfirmRemove}
					isSaving={isSaving}
					item={removeConfig.item}
				/>
			)}

			{importOpened && (
				<ImportSubstituteMaterialsDialog
					open={importOpened}
					onClose={() => setImportOpened(false)}
					onImport={onImport}
					isImporting={isImporting}
				/>
			)}

			{toaster.open && <Toaster toasterOptions={toaster} onClose={() => setToaster({ open: false })} />}
		</>
	)
}

export default Index
